import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, MailPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const OBIETTIVI = ["Sollecito approvazione contenuti", "Conferma appuntamento", "Invio report mensile", "Proposta nuova attività", "Richiesta materiali", "Aggiornamento sul progetto"];
const TONI = [["cordiale", "Cordiale"], ["formale", "Formale"], ["amichevole", "Amichevole"], ["deciso", "Deciso"]];
const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;

export const AiEmailDialog = ({ open, onOpenChange, client, appointment, onSent }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (open) setF({ obiettivo: appointment ? "Conferma appuntamento" : OBIETTIVI[0], tono: "cordiale", punti: "", to: client.email || "", cc: false, oggetto: "", corpo: "" });
  }, [open, client, appointment]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const draft = async () => {
    setBusy("draft");
    try {
      const r = await api.post(`/clients/${client.id}/ai/email-bozza`, { obiettivo: f.obiettivo, tono: f.tono, punti: f.punti, appointment_id: appointment?.id || null });
      setF((p) => ({ ...p, oggetto: r.data.oggetto, corpo: r.data.corpo }));
    } catch (e) { toast.error(errMsg(e, "Generazione non riuscita")); }
    setBusy("");
  };
  const send = async () => {
    if (!f.oggetto.trim() || !f.corpo.trim()) return toast.error("Oggetto e testo sono obbligatori");
    setBusy("send");
    try {
      const r = await api.post(`/clients/${client.id}/email/invia`, { to: f.to.split(/[,;\s]+/).filter(Boolean), cc_soci: f.cc, oggetto: f.oggetto, corpo: f.corpo, obiettivo: f.obiettivo, appointment_id: appointment?.id || null });
      toast.success("Email inviata"); onSent?.(r.data); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl core-scroll" data-testid="ai-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128] flex items-center gap-2"><MailPlus className="h-5 w-5 text-[#E8173A]" />Scrivi email con AI</DialogTitle>
          <DialogDescription>{client.ragione_sociale}{appointment ? ` · ${appointment.titolo}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-[1fr_180px] gap-3">
            <div><L>Obiettivo</L><Input list="ai-email-obiettivi" value={f.obiettivo || ""} onChange={set("obiettivo")} className="rounded-xl" data-testid="ai-email-obiettivo" />
              <datalist id="ai-email-obiettivi">{OBIETTIVI.map((o) => <option key={o} value={o} />)}</datalist></div>
            <div><L>Tono</L><Select value={f.tono} onValueChange={set("tono")}><SelectTrigger className="rounded-xl" data-testid="ai-email-tono"><SelectValue /></SelectTrigger>
              <SelectContent>{TONI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><L>Punti chiave</L><Textarea rows={3} value={f.punti || ""} onChange={set("punti")} placeholder="Es. ricordare la scadenza di venerdì, proporre una call" className="rounded-xl" data-testid="ai-email-punti" /></div>
          <Button onClick={draft} disabled={!!busy || (f.obiettivo || "").trim().length < 3} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid="ai-email-generate">
            {busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{f.corpo ? "Rigenera bozza" : "Genera bozza"}
          </Button>
          <div className="rounded-2xl bg-[#A6DAFC] p-4 space-y-3">
            <div><L>Destinatari</L><Input value={f.to || ""} onChange={set("to")} className="rounded-xl" data-testid="ai-email-to" /></div>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0A1128] cursor-pointer"><Checkbox checked={!!f.cc} onCheckedChange={(v) => set("cc")(!!v)} data-testid="ai-email-cc-soci" />Metti in CC i soci</label>
            <div><L>Oggetto</L><Input value={f.oggetto || ""} onChange={set("oggetto")} className="rounded-xl" data-testid="ai-email-oggetto" /></div>
            <div><L>Testo</L><Textarea rows={10} value={f.corpo || ""} onChange={set("corpo")} className="rounded-xl" data-testid="ai-email-corpo" /></div>
            <Button onClick={send} disabled={!!busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="ai-email-send">{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Invia email</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AiEmailButton = ({ client, appointment, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className={`rounded-full font-bold text-[#0A1128] ${className}`} data-testid="ai-email-open"><Sparkles className="h-4 w-4 mr-1.5" />Scrivi email con AI</Button>
      <AiEmailDialog open={open} onOpenChange={setOpen} client={client} appointment={appointment} />
    </>
  );
};
